// components/PriceVolumeAlertPopup.js
import React, { useState, useMemo, useEffect } from 'react';
import { apiFetch as fetch } from "apiClient";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    RadioGroup,
    FormControlLabel,
    Radio,
    Stack, Paper
} from '@mui/material';
import dayjs from 'dayjs';
import './css/PriceVolumeAlertPopup.css';
import Draggable from "react-draggable";

const PaperComponent = (props) => (
    <Draggable
        handle="#pva-draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"], [class*="MuiDialogActions-root"]'}
    >
        <Paper {...props} />
    </Draggable>
);

const PriceVolumeAlertPopup = ({
                                   open,
                                   onClose,
                                   instrument,
                                   tick,
                                   // NEW: edit support
                                   mode = 'create',           // 'create' | 'edit'
                                   alert = null,              // existing alert when editing
                                   onSaved                    // optional callback(savedAlert)
                               }) => {
    const isEdit = mode === 'edit' || (alert && alert.id);

    const [priceDirection, setPriceDirection] = useState('ABOVE');
    const [priceValue, setPriceValue] = useState('');

    const [volumeDirection, setVolumeDirection] = useState('ABOVE');
    const [volumeValue, setVolumeValue] = useState('');

    const [depthSide, setDepthSide] = useState('SELL'); // top SELL / BUY
    const [depthDirection, setDepthDirection] = useState('ABOVE');
    const [depthValue, setDepthValue] = useState('');

    const [validityType, setValidityType] = useState('TODAY'); // TODAY | FOREVER | TILL_DATE
    const [validTillDate, setValidTillDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const currentPrice = useMemo(
        () =>
            tick && typeof tick.lastTradedPrice === 'number'
                ? tick.lastTradedPrice
                : null,
        [tick]
    );

    const hasAnyCondition =
        !!priceValue ||
        !!volumeValue ||
        !!depthValue;

    // NEW: when editing, preload values from existing alert
    useEffect(() => {
        if (!open) {
            return;
        }

        if (isEdit && alert) {
            setPriceDirection(alert.priceDirection || 'ABOVE');
            setPriceValue(
                alert.priceValue != null ? String(alert.priceValue) : ''
            );

            setVolumeDirection(alert.volumeDirection || 'ABOVE');
            setVolumeValue(
                alert.volumeValue != null ? String(alert.volumeValue) : ''
            );

            setDepthSide(alert.depthSide || 'SELL');
            setDepthDirection(alert.depthDirection || 'ABOVE');
            setDepthValue(
                alert.depthValue != null ? String(alert.depthValue) : ''
            );

            const vt = alert.validityType || 'TODAY';
            setValidityType(vt);

            if (vt === 'TILL_DATE' && alert.validTillDate) {
                setValidTillDate(dayjs(alert.validTillDate).format('YYYY-MM-DD'));
            } else {
                setValidTillDate('');
            }
        } else {
            // fresh create – reset to defaults
            setPriceDirection('ABOVE');
            setPriceValue('');
            setVolumeDirection('ABOVE');
            setVolumeValue('');
            setDepthSide('SELL');
            setDepthDirection('ABOVE');
            setDepthValue('');
            setValidityType('TODAY');
            setValidTillDate('');
            setError(null);
        }
    }, [open, isEdit, alert]);

    const handleSubmit = async () => {
        setError(null);

        if (!hasAnyCondition) {
            setError('Please fill at least one of price / volume / top pending quantity.');
            return;
        }

        if (validityType === 'TILL_DATE' && !validTillDate) {
            setError("Please select 'till date' for validity.");
            return;
        }

        const payload = {
            instrumentToken: instrument.instrumentToken,
            tradingsymbol: instrument.tradingsymbol,
            exchange: instrument.exchange,
            priceCondition: priceValue
                ? {
                    direction: priceDirection, // ABOVE/BELOW
                    value: parseFloat(priceValue)
                }
                : null,
            volumeCondition: volumeValue
                ? {
                    direction: volumeDirection,
                    value: parseFloat(volumeValue)
                }
                : null,
            depthCondition: depthValue
                ? {
                    side: depthSide, // BUY/SELL
                    direction: depthDirection,
                    value: parseInt(depthValue, 10)
                }
                : null,
            validityType, // TODAY | FOREVER | TILL_DATE
            validTillDate: validityType === 'TILL_DATE' ? validTillDate : null
        };

        const url = isEdit && alert
            ? `/api/price-volume-alerts/${alert.id}`
            : '/api/price-volume-alerts';

        const method = isEdit && alert ? 'PUT' : 'POST';

        setSubmitting(true);
        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }

            const saved = await res.json();

            if (onSaved) {
                onSaved(saved);
            }

            onClose();
        } catch (e) {
            console.error('Failed to save alert', e);
            setError(e.message || 'Failed to save alert.');
        } finally {
            setSubmitting(false);
        }
    };

    const todayString = dayjs().format('YYYY-MM-DD');

    return (
        <Dialog
            open={open}
            onClose={(_,reason) => {
                // prevent closing on backdrop click
                if (reason === "backdropClick") return;
                onClose();
            }}
            maxWidth="sm"
            fullWidth
            PaperComponent={PaperComponent}
        >

            <DialogTitle id="pva-draggable-dialog-title">
                <span className="pva-dialog-span">
                    {isEdit ? 'Edit Alert' : 'Create Alert'} - {instrument.tradingsymbol}
                </span>
                <button className="pva-dialog-close-btn" onClick={onClose}>✕</button>
            </DialogTitle>


            <DialogContent dividers className="pva-dialog-content">
                {/* Current price display (disabled) */}
                <TextField
                    label="Current Price"
                    fullWidth
                    margin="dense"
                    size="small"
                    className="pva-current-price"
                    value={currentPrice != null ? currentPrice : 'N/A'}
                    disabled
                />

                <div className="pva-info-title">Alert On
                    <span className={"small-span-pvalert"}>  (any/many of the following) </span>
                </div>
                {/* Price condition */}
                <div className="pva-section-title">Current Traded Price</div>
                <Stack
                    direction="row"
                    spacing={2}
                    className="pva-row"
                    alignItems="flex-start"
                >
                    <FormControl
                        className="pva-field-left"
                        size="small"
                        fullWidth
                    >
                        <InputLabel>Price</InputLabel>
                        <Select
                            label="Price"
                            value={priceDirection}
                            onChange={(e) => setPriceDirection(e.target.value)}
                        >
                            <MenuItem value="ABOVE">Above</MenuItem>
                            <MenuItem value="BELOW">Below</MenuItem>
                        </Select>
                        <FormHelperText>
                            Condition on traded price
                        </FormHelperText>
                    </FormControl>
                    <TextField
                        className="pva-field-right"
                        label="Price"
                        type="number"
                        size="small"
                        value={priceValue}
                        onChange={(e) => setPriceValue(e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                </Stack>

                {/* Volume traded condition */}
                <div className="pva-section-title">Volume condition</div>
                <Stack
                    direction="row"
                    spacing={2}
                    className="pva-row"
                    alignItems="flex-start"
                >
                    <FormControl
                        className="pva-field-left"
                        size="small"
                        fullWidth
                    >
                        <InputLabel>Volume</InputLabel>
                        <Select
                            label="Volume"
                            value={volumeDirection}
                            onChange={(e) => setVolumeDirection(e.target.value)}
                        >
                            <MenuItem value="ABOVE">Above</MenuItem>
                            <MenuItem value="BELOW">Below</MenuItem>
                        </Select>
                        <FormHelperText>
                            Condition on volume traded today
                        </FormHelperText>
                    </FormControl>
                    <TextField
                        className="pva-field-right"
                        label="Volume"
                        type="number"
                        size="small"
                        value={volumeValue}
                        onChange={(e) => setVolumeValue(e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                </Stack>

                {/* Top side pending quantity condition */}
                <div className="pva-section-title">Top depth condition</div>
                <Stack
                    direction="row"
                    spacing={2}
                    className="pva-row"
                    alignItems="flex-start"
                >
                    <FormControl
                        className="pva-field-left"
                        size="small"
                        fullWidth
                    >
                        <InputLabel>Side</InputLabel>
                        <Select
                            label="Side"
                            value={depthSide}
                            onChange={(e) => setDepthSide(e.target.value)}
                        >
                            <MenuItem value="BUY">Buy side</MenuItem>
                            <MenuItem value="SELL">Sell side</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl
                        className="pva-field-middle"
                        size="small"
                        fullWidth
                    >
                        <InputLabel>Pending Qty</InputLabel>
                        <Select
                            label="Pending Qty"
                            value={depthDirection}
                            onChange={(e) => setDepthDirection(e.target.value)}
                        >
                            <MenuItem value="ABOVE">Above</MenuItem>
                            <MenuItem value="BELOW">Below</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        className="pva-field-right"
                        label="Quantity"
                        type="number"
                        size="small"
                        value={depthValue}
                        onChange={(e) => setDepthValue(e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                </Stack>

                {/* Validity */}
                <div className="pva-section-title">Validity</div>
                <div className="pva-validity-row">
                    <RadioGroup
                        row
                        value={validityType}
                        onChange={(e) => setValidityType(e.target.value)}
                    >
                        <FormControlLabel
                            value="TODAY"
                            control={<Radio size="small" />}
                            label="Today"
                        />
                        <FormControlLabel
                            value="FOREVER"
                            control={<Radio size="small" />}
                            label="Forever"
                        />
                        <FormControlLabel
                            value="TILL_DATE"
                            control={<Radio size="small" />}
                            label="Till date"
                        />
                    </RadioGroup>
                </div>

                {validityType === 'TILL_DATE' && (
                    <TextField
                        label="Valid till"
                        type="date"
                        margin="dense"
                        fullWidth
                        size="small"
                        className="pva-date-field"
                        value={validTillDate}
                        onChange={(e) => setValidTillDate(e.target.value)}
                        inputProps={{ min: todayString }}
                        InputLabelProps={{ shrink: true }}
                    />
                )}

                {error && (
                    <FormHelperText error sx={{ mt: 1.5 }}>
                        {error}
                    </FormHelperText>
                )}
            </DialogContent>
            <DialogActions>
                {isEdit && (
                    <Button
                        onClick={async () => {
                            if (!window.confirm("Delete this alert permanently?")) return;
                            try {
                                const res = await fetch(`/api/price-volume-alerts/${alert.id}`, {
                                    method: 'DELETE'
                                });
                                if (!res.ok) {
                                    const text = await res.text();
                                    throw new Error(text || "Failed to delete alert");
                                }
                                if (onSaved) onSaved({ deleted: alert.id });
                                onClose();
                            } catch (e) {
                                console.error("Failed to delete alert", e);
                                // you can also surface this via setError if you want
                            }
                        }}
                        style={{ background:"#ef5350", color:"white" }}
                        variant="contained"
                    >
                        Delete
                    </Button>
                )}


                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>

                <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    variant="contained"
                    color="primary"
                >
                    {isEdit ? 'Save Changes' : 'Save Alert'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PriceVolumeAlertPopup;
